/**
 * Dashboard — Full dynamic widget dashboard with drag-and-drop grid layout
 *
 * Features:
 * - ResponsiveGridLayout with 12-column grid
 * - Edit mode for layout customization
 * - Widget selector, time range, saved layouts
 * - Expanded widget modal
 * - SSE real-time metrics
 */

import React, { useState, useCallback, useMemo, useRef } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Fade,
  Backdrop,
  Tooltip,
  TextField,
  Select,
  FormControl,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  FolderOpen as FolderIcon,
  Delete as DeleteIcon,
  FiberManualRecord as DotIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import DashboardNavbar from "components/DashboardNavbar";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import { DashboardProvider, useDashboard } from "./context/DashboardContext";
import { WidgetRenderer, WidgetSelector } from "./components";
import { getWidgetById, DEFAULT_SIZES } from "./config/widgetRegistry";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

const TIME_RANGES = [
  { value: "1h", labelKey: "dashboard.timeRange.1h" },
  { value: "6h", labelKey: "dashboard.timeRange.6h" },
  { value: "24h", labelKey: "dashboard.timeRange.24h" },
  { value: "7d", labelKey: "dashboard.timeRange.7d" },
  { value: "30d", labelKey: "dashboard.timeRange.30d" },
  { value: "90d", labelKey: "dashboard.timeRange.90d" },
];

/* ─── Expanded Widget Modal ───────────────────────────────────── */

function ExpandedWidgetModal({ widgetId, onClose }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const widget = widgetId ? getWidgetById(widgetId) : null;
  if (!widget) return null;

  return (
    <Modal
      open={!!widgetId}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 300,
          sx: { backdropFilter: "blur(8px)", bgcolor: "rgba(0, 0, 0, 0.7)" },
        },
      }}
    >
      <Fade in={!!widgetId}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            maxWidth: 1200,
            height: "85vh",
            maxHeight: 900,
            bgcolor: darkMode ? "#0F0F1A" : "#ffffff",
            borderRadius: "20px",
            border: darkMode ? "1px solid rgba(139, 92, 246, 0.2)" : "1px solid rgba(0,0,0,0.1)",
            boxShadow: darkMode ? "0 40px 80px -20px rgba(0, 0, 0, 0.8)" : "0 40px 80px -20px rgba(0, 0, 0, 0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <KDTypography variant="h6" sx={{ color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontWeight: 700 }}>
              {widget.title}
            </KDTypography>
            <IconButton onClick={onClose} sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)", "&:hover": { color: "#EF4444" } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
            <WidgetRenderer widgetId={widgetId} isExpanded />
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

/* ─── Time Range Selector ─────────────────────────────────────── */

function TimeRangeSelector({ value, onChange }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  // Extract preset string from timeRange object
  const preset = typeof value === "object" ? value?.preset : value;
  return (
    <FormControl size="small">
      <Select
        value={preset || "7d"}
        onChange={(e) => onChange({ preset: e.target.value })}
        displayEmpty
        sx={{
          color: darkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
          fontSize: "0.8rem",
          borderRadius: "10px",
          bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(139, 92, 246, 0.3)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#7C3AED", borderWidth: 1 },
          "& .MuiSvgIcon-root": { color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)" },
          minWidth: 140,
        }}
        startAdornment={<ScheduleIcon sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.3)", mr: 0.5, fontSize: 16 }} />}
      >
        {TIME_RANGES.map((tr) => (
          <MenuItem key={tr.value} value={tr.value}>{t(tr.labelKey)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/* ─── Save Layout Dialog ──────────────────────────────────────── */

function SaveLayoutPopover({ open, anchorEl, onClose, onSave }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [name, setName] = useState("");
  const t = useT();

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
      setName("");
      onClose();
    }
  };

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: darkMode ? "#1A1A2E" : "#ffffff",
          border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          p: 2,
          minWidth: 260,
        },
      }}
    >
      <KDTypography variant="subtitle2" sx={{ color: darkMode ? "white" : "rgba(0,0,0,0.87)", mb: 1.5 }}>
        {t("dashboard.saveLayoutAs")}
      </KDTypography>
      <TextField
        fullWidth
        size="small"
        placeholder={t("dashboard.layoutNamePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
        autoFocus
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
            borderRadius: "8px",
            "& fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" },
            "&.Mui-focused fieldset": { borderColor: "#7C3AED" },
          },
          "& .MuiOutlinedInput-input": { color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontSize: "0.85rem" },
        }}
      />
      <Box
        onClick={handleSave}
        sx={{
          px: 2,
          py: 1,
          borderRadius: "8px",
          background: name.trim() ? "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)" : "rgba(255,255,255,0.05)",
          cursor: name.trim() ? "pointer" : "default",
          textAlign: "center",
          transition: "all 0.2s",
        }}
      >
        <KDTypography variant="body2" sx={{ color: "white", fontWeight: 600, opacity: name.trim() ? 1 : 0.4 }}>
          {t("common.save")}
        </KDTypography>
      </Box>
    </Menu>
  );
}

/* ─── Saved Layouts Menu ──────────────────────────────────────── */

function SavedLayoutsMenu({ open, anchorEl, onClose, layouts, currentLayoutName, onLoad, onDelete }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: darkMode ? "#1A1A2E" : "#ffffff",
          border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          minWidth: 220,
        },
      }}
    >
      {layouts.length === 0 ? (
        <MenuItem disabled>
          <KDTypography variant="body2" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>{t("dashboard.noSavedLayouts")}</KDTypography>
        </MenuItem>
      ) : (
        layouts.map((l) => (
          <MenuItem
            key={l.name}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              "&:hover": { bgcolor: "rgba(139, 92, 246, 0.1)" },
            }}
          >
            <Box
              onClick={() => { onLoad(l.name); onClose(); }}
              sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              {l.name === currentLayoutName && <DotIcon sx={{ color: "#7C3AED", fontSize: 10 }} />}
              <ListItemText
                primary={l.name}
                sx={{ "& .MuiTypography-root": { color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontSize: "0.85rem" } }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(l.name); }}
              sx={{ color: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)", "&:hover": { color: "#EF4444" } }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </MenuItem>
        ))
      )}
    </Menu>
  );
}

/* ─── Edit Mode Banner ────────────────────────────────────────── */

function EditModeBanner({ onDone, onAddWidget, onReset }) {
  const t = useT();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 1.5,
        mb: 2,
        borderRadius: "14px",
        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.1) 100%)",
        border: "2px dashed rgba(139, 92, 246, 0.4)",
        animation: "editPulse 2s ease-in-out infinite",
        "@keyframes editPulse": {
          "0%, 100%": { borderColor: "rgba(139, 92, 246, 0.4)" },
          "50%": { borderColor: "rgba(139, 92, 246, 0.7)" },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <EditIcon sx={{ color: "#A78BFA", fontSize: 20 }} />
        <KDTypography variant="body2" sx={{ color: "white", fontWeight: 600 }}>
          {t("dashboard.editMode")} — {t("dashboard.editModeHint")}
        </KDTypography>
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title={t("dashboard.addWidgets")}>
          <IconButton
            onClick={onAddWidget}
            sx={{
              bgcolor: "rgba(16, 185, 129, 0.15)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              color: "#10B981",
              "&:hover": { bgcolor: "rgba(16, 185, 129, 0.25)" },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("dashboard.resetToDefault")}>
          <IconButton
            onClick={onReset}
            sx={{
              bgcolor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              color: "#EF4444",
              "&:hover": { bgcolor: "rgba(239, 68, 68, 0.2)" },
            }}
          >
            <RestoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("dashboard.doneEditing")}>
          <IconButton
            onClick={onDone}
            sx={{
              bgcolor: "rgba(139, 92, 246, 0.2)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              color: "#A78BFA",
              "&:hover": { bgcolor: "rgba(139, 92, 246, 0.3)" },
            }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

/* ─── Connection Status ───────────────────────────────────────── */

function ConnectionStatus({ connected, lastUpdate }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  const timeAgo = lastUpdate ? Math.round((Date.now() - new Date(lastUpdate).getTime()) / 1000) : null;

  return (
    <Tooltip title={connected ? t("dashboard.liveUpdated", { seconds: timeAgo ?? "" }) : t("dashboard.disconnected")}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: connected ? "#10B981" : "#EF4444",
            boxShadow: connected ? "0 0 8px rgba(16, 185, 129, 0.5)" : "0 0 8px rgba(239, 68, 68, 0.5)",
            animation: connected ? "livePulse 2s ease-in-out infinite" : "none",
            "@keyframes livePulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.5 },
            },
          }}
        />
        <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", fontSize: "0.7rem" }}>
          {connected ? t("dashboard.live") : t("dashboard.offline")}
        </KDTypography>
      </Box>
    </Tooltip>
  );
}

/* ─── Dashboard Content (inside DashboardProvider) ────────────── */

function DashboardContent() {
  const { state, actions, metricsData, streamConnected, lastUpdate } = useDashboard();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  const {
    layout,
    isEditingMode,
    activeWidgets,
    expandedWidget,
    widgetSelectorOpen,
    timeRange,
    savedLayouts,
    currentLayoutName,
    loading,
  } = state;

  // Menus
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [saveAnchor, setSaveAnchor] = useState(null);
  const [layoutsAnchor, setLayoutsAnchor] = useState(null);

  const gridRef = useRef(null);

  // Compute grid layout items
  const gridLayout = useMemo(() => {
    if (!layout || !Array.isArray(layout)) return [];
    return layout.filter((item) => activeWidgets?.includes(item.i));
  }, [layout, activeWidgets]);

  // Handle layout change from react-grid-layout
  const handleLayoutChange = useCallback(
    (newLayout) => {
      if (isEditingMode) {
        actions.updateLayout(newLayout);
      }
    },
    [isEditingMode, actions]
  );

  // Breakpoints and column config
  const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
  const cols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };

  return (
    <>
      {/* Top toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DashboardIcon sx={{ color: "#7C3AED", fontSize: 28 }} />
            <KDTypography variant="h5" sx={{ color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontWeight: 700, letterSpacing: "-0.01em" }}>
              {t("dashboard.title")}
            </KDTypography>
          </Box>
          <ConnectionStatus connected={streamConnected} lastUpdate={lastUpdate} />
          {currentLayoutName && (
            <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", px: 1.5, py: 0.5, borderRadius: "6px" }}>
              {currentLayoutName}
            </KDTypography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TimeRangeSelector value={timeRange} onChange={actions.setTimeRange} />

          {!isEditingMode && (
            <Tooltip title={t("dashboard.editLayout")}>
              <IconButton
                onClick={actions.enterEditMode}
                sx={{
                  bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                  border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "10px",
                  color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(139, 92, 246, 0.15)", borderColor: "rgba(139, 92, 246, 0.3)", color: "#A78BFA" },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={t("dashboard.moreOptions")}>
            <IconButton
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              sx={{
                bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
                borderRadius: "10px",
                color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)" },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* More Menu */}
          <Menu
            open={!!moreAnchor}
            anchorEl={moreAnchor}
            onClose={() => setMoreAnchor(null)}
            PaperProps={{
              sx: {
                bgcolor: darkMode ? "#1A1A2E" : "#ffffff",
                border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
                borderRadius: "12px",
                minWidth: 180,
              },
            }}
          >
            <MenuItem
              onClick={(e) => { setMoreAnchor(null); setSaveAnchor(e.currentTarget); }}
              sx={{ "&:hover": { bgcolor: "rgba(139, 92, 246, 0.1)" } }}
            >
              <ListItemIcon><SaveIcon sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)" }} fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ "& .MuiTypography-root": { color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontSize: "0.85rem" } }}>{t("dashboard.saveLayout")}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={(e) => { setMoreAnchor(null); setLayoutsAnchor(e.currentTarget); }}
              sx={{ "&:hover": { bgcolor: "rgba(139, 92, 246, 0.1)" } }}
            >
              <ListItemIcon><FolderIcon sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)" }} fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ "& .MuiTypography-root": { color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontSize: "0.85rem" } }}>{t("dashboard.loadLayout")}</ListItemText>
            </MenuItem>
            <Divider sx={{ borderColor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }} />
            <MenuItem
              onClick={() => { setMoreAnchor(null); actions.refreshData(); }}
              sx={{ "&:hover": { bgcolor: "rgba(139, 92, 246, 0.1)" } }}
            >
              <ListItemIcon><RestoreIcon sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)" }} fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ "& .MuiTypography-root": { color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontSize: "0.85rem" } }}>{t("dashboard.refreshData")}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Edit mode banner */}
      {isEditingMode && (
        <EditModeBanner
          onDone={actions.exitEditMode}
          onAddWidget={actions.openWidgetSelector}
          onReset={actions.resetToDefault}
        />
      )}

      {/* Loading state */}
      {loading && !metricsData && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
          <KDTypography variant="body1" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}>
            {t("dashboard.loading")}
          </KDTypography>
        </Box>
      )}

      {/* Widget grid */}
      {activeWidgets && activeWidgets.length > 0 ? (
        <Box
          ref={gridRef}
          sx={{
            "& .react-grid-item": {
              transition: isEditingMode ? "none" : "all 200ms ease",
            },
            "& .react-grid-item.react-grid-placeholder": {
              background: "rgba(139, 92, 246, 0.2) !important",
              border: "2px dashed rgba(139, 92, 246, 0.5) !important",
              borderRadius: "16px !important",
              opacity: "1 !important",
            },
            "& .react-grid-item > .react-resizable-handle": {
              opacity: isEditingMode ? 0.5 : 0,
              transition: "opacity 0.2s ease",
            },
            "& .react-grid-item:hover > .react-resizable-handle": {
              opacity: isEditingMode ? 1 : 0,
            },
            "& .react-resizable-handle::after": {
              borderColor: "rgba(139, 92, 246, 0.5) !important",
            },
          }}
        >
          <ResponsiveGridLayout
            layouts={{ lg: gridLayout }}
            breakpoints={breakpoints}
            cols={cols}
            rowHeight={60}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={isEditingMode}
            isResizable={isEditingMode}
            draggableHandle=".widget-drag-handle"
            onLayoutChange={handleLayoutChange}
            useCSSTransforms
            compactType="vertical"
          >
            {activeWidgets.map((widgetId) => {
              const widget = getWidgetById(widgetId);
              if (!widget) return null;
              const size = DEFAULT_SIZES[widget.type] || { w: 3, h: 2 };
              const layoutItem = gridLayout.find((item) => item.i === widgetId);
              return (
                <div
                  key={widgetId}
                  data-grid={{
                    x: layoutItem?.x ?? 0,
                    y: layoutItem?.y ?? 0,
                    w: layoutItem?.w ?? size.w,
                    h: layoutItem?.h ?? size.h,
                    minW: size.minW || 2,
                    minH: size.minH || 2,
                  }}
                >
                  <Box sx={{ height: "100%" }}>
                    <WidgetRenderer widgetId={widgetId} />
                  </Box>
                </div>
              );
            })}
          </ResponsiveGridLayout>
        </Box>
      ) : (
        !loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              borderRadius: "20px",
              border: darkMode ? "2px dashed rgba(255,255,255,0.1)" : "2px dashed rgba(0,0,0,0.1)",
              bgcolor: darkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
            }}
          >
            <DashboardIcon sx={{ fontSize: 64, color: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)", mb: 2 }} />
            <KDTypography variant="h6" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", mb: 1 }}>
              {t("dashboard.noWidgets")}
            </KDTypography>
            <KDTypography variant="body2" sx={{ color: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)", mb: 3 }}>
              {t("dashboard.noWidgetsHint")}
            </KDTypography>
            <Box
              onClick={() => { actions.enterEditMode(); actions.openWidgetSelector(); }}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(124, 58, 237, 0.4)",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(124, 58, 237, 0.5)" },
                transition: "all 0.2s ease",
              }}
            >
              <KDTypography variant="button" sx={{ color: "white", fontWeight: 600 }}>
                {t("dashboard.addWidgets")}
              </KDTypography>
            </Box>
          </Box>
        )
      )}

      {/* Widget Selector Modal */}
      <WidgetSelector open={widgetSelectorOpen} onClose={actions.closeWidgetSelector} />

      {/* Expanded Widget Modal */}
      <ExpandedWidgetModal widgetId={expandedWidget} onClose={actions.collapseWidget} />

      {/* Save Layout Popover */}
      <SaveLayoutPopover
        open={!!saveAnchor}
        anchorEl={saveAnchor}
        onClose={() => setSaveAnchor(null)}
        onSave={(name) => actions.saveNamedLayout(name)}
      />

      {/* Saved Layouts Menu */}
      <SavedLayoutsMenu
        open={!!layoutsAnchor}
        anchorEl={layoutsAnchor}
        onClose={() => setLayoutsAnchor(null)}
        layouts={savedLayouts || []}
        currentLayoutName={currentLayoutName}
        onLoad={(name) => actions.loadSavedLayout(name)}
        onDelete={(name) => actions.deleteLayout(name)}
      />
    </>
  );
}

/* ─── Dashboard Page (outer wrapper) ──────────────────────────── */

function Dashboard() {
  const [controller] = useMaterialUIController();
  const { miniSidenav } = controller;

  return (
    <KDBox
      sx={({ breakpoints, transitions }) => ({
        p: 3,
        marginLeft: miniSidenav ? "80px" : "260px",
        transition: transitions.create("margin-left", {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
        [breakpoints.down("xl")]: { marginLeft: 0 },
        minHeight: "100vh",
      })}
    >
      <DashboardNavbar />
      <KDBox mt={2}>
        <DashboardProvider>
          <DashboardContent />
        </DashboardProvider>
      </KDBox>
    </KDBox>
  );
}

export default Dashboard;
